import React, { useState, useEffect } from 'react';
import {
  Modal,
  Input,
  DatePicker,
  Select,
  Divider,
  Spin,
  Upload,
  App,
  Tabs,
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  UploadOutlined,
  FileOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { ThemeButton } from '../components';
import { uploadImage } from '../../helpers/helpers';
import {
  useUpdateDogMutation,
  useDeleteDogMutation,
  useRestoreDogMutation,
} from '../../services/dogsApi';
import { US_STATES } from '../../constants/constants';

const { TextArea } = Input;
const { Option } = Select;

// Helper to check vaccination expiration status
const getVaccinationStatus = vaccinationDate => {
  if (!vaccinationDate) return { isExpired: false, isExpiringSoon: false };

  const today = dayjs().startOf('day');
  const expDate = dayjs(vaccinationDate).startOf('day');
  const daysUntilExpiration = expDate.diff(today, 'day');

  return {
    isExpired: daysUntilExpiration < 0,
    isExpiringSoon: daysUntilExpiration >= 0 && daysUntilExpiration <= 30,
    daysUntilExpiration,
  };
};

const EditDogModal = ({
  isOpen,
  onClose,
  data,
  onSuccess,
  initialTab = '1',
  zIndex,
}) => {
  const { message } = App.useApp();
  const [formData, setFormData] = useState(null);
  const [activeTab, setActiveTab] = useState(initialTab);
  const [uploading, setUploading] = useState(false);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [deleteConfirmVisible, setDeleteConfirmVisible] = useState(false);
  const [restoreConfirmVisible, setRestoreConfirmVisible] = useState(false);
  const [updateDog, { isLoading: updating }] = useUpdateDogMutation();
  const [deleteDog, { isLoading: deleting }] = useDeleteDogMutation();
  const [restoreDog, { isLoading: restoring }] = useRestoreDogMutation();

  // Initialize form data when modal opens or data changes
  useEffect(() => {
    if (data && isOpen) {
      setFormData({
        dog_name: data.dog_name || '',
        breed: data.breed || '',
        gender: data.gender || '',
        weight: data.weight || '',
        birthday: data.birthday || '',
        color: data.color || '',
        about: data.about || '',
        attributes: data.attributes || [],
        personality: data.personality || [],
        veterinarian: {
          name: data.veterinarian?.name || '',
          phone_number: data.veterinarian?.phone_number || '',
          clinic_name: data.veterinarian?.clinic_name || '',
          street: data.veterinarian?.street || '',
          city: data.veterinarian?.city || '',
          state: data.veterinarian?.state || '',
        },
        emergency_contact: {
          name: data.emergency_contact?.name || '',
          number: data.emergency_contact?.number || '',
          relationship: data.emergency_contact?.relationship || '',
        },
        medical_problem: {
          yes: data.medical_problem?.yes || false,
          details: data.medical_problem?.details || '',
        },
        regular_medication: {
          yes: data.regular_medication?.yes || false,
          details: data.regular_medication?.details || '',
        },
        medication_during_stay: {
          yes: data.medication_during_stay?.yes || false,
          details: data.medication_during_stay?.details || '',
        },
        medical_attachments: data.medical_attachments || [],
        attachments: data.attachments || [],
        avatar: data.avatar || '',
      });
    }
  }, [data, isOpen]);

  // Update active tab when initialTab prop changes (e.g., opening to vaccinations tab)
  useEffect(() => {
    if (isOpen) {
      setActiveTab(initialTab);
    }
  }, [isOpen, initialTab]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleNestedChange = (parent, field, value) => {
    setFormData(prev => ({
      ...prev,
      [parent]: {
        ...prev[parent],
        [field]: value,
      },
    }));
  };

  // Vaccination handling
  const handleAddVaccination = () => {
    setFormData(prev => ({
      ...prev,
      medical_attachments: [
        ...prev.medical_attachments,
        {
          vaccination_title: '',
          vaccination_date: '',
          file: '',
        },
      ],
    }));
  };

  const handleVaccinationChange = (index, field, value) => {
    setFormData(prev => {
      const newAttachments = [...prev.medical_attachments];
      newAttachments[index] = {
        ...newAttachments[index],
        [field]: value,
      };
      return {
        ...prev,
        medical_attachments: newAttachments,
      };
    });
  };

  const handleRemoveVaccination = index => {
    setFormData(prev => ({
      ...prev,
      medical_attachments: prev.medical_attachments.filter(
        (_, i) => i !== index
      ),
    }));
  };

  // Document handling
  const handleAddDocument = async file => {
    setUploading(true);
    try {
      const url = await uploadImage(file, file.name);
      if (url) {
        const fileType = file.type.includes('image') ? 'image' : 'document';
        setFormData(prev => ({
          ...prev,
          attachments: [
            ...prev.attachments,
            {
              url,
              name: file.name,
              type: fileType,
            },
          ],
        }));
        message.success('Document uploaded successfully');
      } else {
        message.error('Failed to upload document');
      }
    } catch (error) {
      message.error('Error uploading document');
    }
    setUploading(false);
    return false; // Prevent default upload behavior
  };

  const handleRemoveDocument = index => {
    setFormData(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index),
    }));
  };

  // Avatar upload
  const handleAvatarUpload = async file => {
    setAvatarUploading(true);
    try {
      const url = await uploadImage(file, file.name);
      if (url) {
        setFormData(prev => ({
          ...prev,
          avatar: url,
        }));
        message.success('Avatar uploaded successfully');
      } else {
        message.error('Failed to upload avatar');
      }
    } catch (error) {
      message.error('Error uploading avatar');
    }
    setAvatarUploading(false);
    return false;
  };

  // Submit form
  const handleSubmit = async () => {
    try {
      // Remove computed/read-only fields before sending
      const {
        vaccination_status,
        owner,
        owner_id,
        _id,
        created_at,
        updated_at,
        active,
        ...cleanFormData
      } = formData;

      const result = await updateDog({
        id: data._id,
        body: cleanFormData,
      }).unwrap();

      if (result.error) {
        message.error(result.msg || 'Failed to update dog');
      } else {
        message.success('Dog updated successfully');
        onSuccess?.();
        onClose();
      }
    } catch (error) {
      message.error('Error updating dog');
      console.error('Update error:', error);
    }
  };

  // Delete dog handler
  const handleDelete = async () => {
    try {
      const result = await deleteDog(data._id).unwrap();
      if (result.error === true) {
        message.error(result.msg || 'Failed to delete dog');
        setDeleteConfirmVisible(false);
      } else {
        message.success(result.msg || 'Dog deleted successfully');
        // Close both modals with slight delay to ensure proper cleanup
        setDeleteConfirmVisible(false);
        setTimeout(() => {
          onClose();
          onSuccess?.();
        }, 100);
      }
    } catch (error) {
      message.error(error.data?.msg || 'Error deleting dog');
      console.error('Delete error:', error);
      setDeleteConfirmVisible(false);
    }
  };

  // Restore dog handler
  const handleRestore = async () => {
    try {
      const result = await restoreDog(data._id).unwrap();
      if (result.error === true) {
        message.error(result.msg || 'Failed to restore dog');
        setRestoreConfirmVisible(false);
      } else {
        message.success(result.msg || 'Dog restored successfully');
        setRestoreConfirmVisible(false);
        setTimeout(() => {
          onClose();
          onSuccess?.();
        }, 100);
      }
    } catch (error) {
      message.error(error.data?.msg || 'Error restoring dog');
      console.error('Restore error:', error);
      setRestoreConfirmVisible(false);
    }
  };

  if (!formData) return null;

  const isDeleted = data?.active === false;

  const tabItems = [
    {
      key: '1',
      label: 'Basic Info',
      children: (
        <div className="space-y-4">
          {/* Avatar */}
          <div className="flex items-center gap-4 mb-6">
            <div className="relative">
              {formData.avatar ? (
                <img
                  src={formData.avatar}
                  alt="Dog avatar"
                  className="w-24 h-24 rounded-full object-cover"
                />
              ) : (
                <div className="w-24 h-24 rounded-full bg-gray-200 flex items-center justify-center">
                  <span className="text-gray-500">No image</span>
                </div>
              )}
            </div>
            <Upload
              beforeUpload={handleAvatarUpload}
              showUploadList={false}
              accept="image/*"
            >
              <ThemeButton
                content={
                  avatarUploading ? <Spin size="small" /> : 'Change Photo'
                }
                className="!h-8"
              />
            </Upload>
          </div>

          {/* Name & Breed */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">
                Dog Name
              </label>
              <Input
                value={formData.dog_name}
                onChange={e => handleInputChange('dog_name', e.target.value)}
                placeholder="Enter dog name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">
                Breed
              </label>
              <Input
                value={formData.breed}
                onChange={e => handleInputChange('breed', e.target.value)}
                placeholder="Enter breed"
              />
            </div>
          </div>

          {/* Gender & Weight */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">
                Gender
              </label>
              <Select
                value={formData.gender}
                onChange={value => handleInputChange('gender', value)}
                className="w-full"
                placeholder="Select gender"
              >
                <Option value="Male">Male</Option>
                <Option value="Female">Female</Option>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">
                Weight (lbs)
              </label>
              <Input
                type="number"
                value={formData.weight}
                onChange={e => handleInputChange('weight', e.target.value)}
                placeholder="Enter weight"
              />
            </div>
          </div>

          {/* Birthday & Color */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">
                Birthday
              </label>
              <DatePicker
                value={formData.birthday ? dayjs(formData.birthday) : null}
                onChange={(date, dateString) =>
                  handleInputChange('birthday', dateString)
                }
                className="w-full"
                placeholder="Select birthday"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">
                Color
              </label>
              <Input
                value={formData.color}
                onChange={e => handleInputChange('color', e.target.value)}
                placeholder="Enter color"
              />
            </div>
          </div>

          {/* About */}
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">
              About
            </label>
            <TextArea
              value={formData.about}
              onChange={e => handleInputChange('about', e.target.value)}
              placeholder="Tell us about this dog..."
              rows={3}
            />
          </div>
        </div>
      ),
    },
    {
      key: '2',
      label: 'Contacts',
      children: (
        <div className="space-y-6">
          {/* Veterinarian */}
          <div>
            <h3 className="text-lg font-semibold mb-3">Veterinarian</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">
                  Name
                </label>
                <Input
                  value={formData.veterinarian.name}
                  onChange={e =>
                    handleNestedChange('veterinarian', 'name', e.target.value)
                  }
                  placeholder="e.g. Dr. Appleseed"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">
                  Phone Number
                </label>
                <Input
                  value={formData.veterinarian.phone_number}
                  onChange={e =>
                    handleNestedChange(
                      'veterinarian',
                      'phone_number',
                      e.target.value
                    )
                  }
                  placeholder="Phone number"
                />
              </div>
            </div>
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-600 mb-1">
                Clinic Name
              </label>
              <Input
                value={formData.veterinarian.clinic_name}
                onChange={e =>
                  handleNestedChange(
                    'veterinarian',
                    'clinic_name',
                    e.target.value
                  )
                }
                placeholder="e.g. Appleseed Animal Hospital"
              />
            </div>
            <div className="grid grid-cols-3 gap-4 mt-4">
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">
                  Street
                </label>
                <Input
                  value={formData.veterinarian.street}
                  onChange={e =>
                    handleNestedChange('veterinarian', 'street', e.target.value)
                  }
                  placeholder="e.g. 123 Main St"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">
                  City
                </label>
                <Input
                  value={formData.veterinarian.city}
                  onChange={e =>
                    handleNestedChange('veterinarian', 'city', e.target.value)
                  }
                  placeholder="City"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">
                  State
                </label>
                <Select
                  value={formData.veterinarian.state}
                  onChange={value =>
                    handleNestedChange('veterinarian', 'state', value)
                  }
                  className="w-full"
                  placeholder="Select state"
                  showSearch
                  optionFilterProp="children"
                >
                  {US_STATES.map(state => (
                    <Option key={state.abbreviation} value={state.abbreviation}>
                      {state.abbreviation} - {state.name}
                    </Option>
                  ))}
                </Select>
              </div>
            </div>
          </div>

          <Divider />

          {/* Emergency Contact */}
          <div>
            <h3 className="text-lg font-semibold mb-3">Emergency Contact</h3>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">
                  Name
                </label>
                <Input
                  value={formData.emergency_contact.name}
                  onChange={e =>
                    handleNestedChange(
                      'emergency_contact',
                      'name',
                      e.target.value
                    )
                  }
                  placeholder="Contact name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">
                  Phone Number
                </label>
                <Input
                  value={formData.emergency_contact.number}
                  onChange={e =>
                    handleNestedChange(
                      'emergency_contact',
                      'number',
                      e.target.value
                    )
                  }
                  placeholder="Phone number"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">
                  Relationship
                </label>
                <Input
                  value={formData.emergency_contact.relationship}
                  onChange={e =>
                    handleNestedChange(
                      'emergency_contact',
                      'relationship',
                      e.target.value
                    )
                  }
                  placeholder="e.g., Spouse, Friend"
                />
              </div>
            </div>
          </div>
        </div>
      ),
    },
    {
      key: '3',
      label: 'Medical',
      children: (
        <div className="space-y-6">
          {/* Medical Problems */}
          <div>
            <h3 className="text-lg font-semibold mb-3">Medical Problems</h3>
            <div className="flex items-center gap-4 mb-2">
              <label className="text-sm text-gray-600">
                Does this dog have any medical problems?
              </label>
              <Select
                value={formData.medical_problem.yes ? 'yes' : 'no'}
                onChange={value =>
                  handleNestedChange('medical_problem', 'yes', value === 'yes')
                }
                className="w-24"
              >
                <Option value="yes">Yes</Option>
                <Option value="no">No</Option>
              </Select>
            </div>
            {formData.medical_problem.yes && (
              <TextArea
                value={formData.medical_problem.details}
                onChange={e =>
                  handleNestedChange(
                    'medical_problem',
                    'details',
                    e.target.value
                  )
                }
                placeholder="Please describe the medical problems..."
                rows={2}
              />
            )}
          </div>

          <Divider />

          {/* Regular Medication */}
          <div>
            <h3 className="text-lg font-semibold mb-3">Regular Medication</h3>
            <div className="flex items-center gap-4 mb-2">
              <label className="text-sm text-gray-600">
                Is this dog on any regular medication?
              </label>
              <Select
                value={formData.regular_medication.yes ? 'yes' : 'no'}
                onChange={value =>
                  handleNestedChange(
                    'regular_medication',
                    'yes',
                    value === 'yes'
                  )
                }
                className="w-24"
              >
                <Option value="yes">Yes</Option>
                <Option value="no">No</Option>
              </Select>
            </div>
            {formData.regular_medication.yes && (
              <TextArea
                value={formData.regular_medication.details}
                onChange={e =>
                  handleNestedChange(
                    'regular_medication',
                    'details',
                    e.target.value
                  )
                }
                placeholder="Please describe the medication..."
                rows={2}
              />
            )}
          </div>

          <Divider />

          {/* Medication During Stay */}
          <div>
            <h3 className="text-lg font-semibold mb-3">
              Medication During Stay
            </h3>
            <div className="flex items-center gap-4 mb-2">
              <label className="text-sm text-gray-600">
                Will this dog need medication during their stay?
              </label>
              <Select
                value={formData.medication_during_stay.yes ? 'yes' : 'no'}
                onChange={value =>
                  handleNestedChange(
                    'medication_during_stay',
                    'yes',
                    value === 'yes'
                  )
                }
                className="w-24"
              >
                <Option value="yes">Yes</Option>
                <Option value="no">No</Option>
              </Select>
            </div>
            {formData.medication_during_stay.yes && (
              <TextArea
                value={formData.medication_during_stay.details}
                onChange={e =>
                  handleNestedChange(
                    'medication_during_stay',
                    'details',
                    e.target.value
                  )
                }
                placeholder="Please describe the medication and instructions..."
                rows={2}
              />
            )}
          </div>
        </div>
      ),
    },
    {
      key: '4',
      label: 'Vaccinations',
      children: (
        <div className="space-y-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">Vaccination Records</h3>
            <ThemeButton
              content={
                <>
                  <PlusOutlined className="mr-1" /> Add Vaccination
                </>
              }
              className="!h-8"
              onClick={handleAddVaccination}
            />
          </div>

          {formData.medical_attachments.length === 0 ? (
            <div className="text-center py-8 bg-gray-50 rounded-lg">
              <p className="text-gray-500">No vaccination records</p>
              <p className="text-gray-400 text-sm mt-1">
                Click "Add Vaccination" to add one
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {formData.medical_attachments.map((vaccination, index) => {
                const status = getVaccinationStatus(
                  vaccination.vaccination_date
                );
                return (
                  <div
                    key={index}
                    className={`border rounded-lg p-4 relative ${
                      status.isExpired
                        ? 'bg-red-50 border-red-300'
                        : status.isExpiringSoon
                          ? 'bg-orange-50 border-orange-300'
                          : 'bg-gray-50'
                    }`}
                  >
                    <button
                      onClick={() => handleRemoveVaccination(index)}
                      className="absolute top-2 right-2 text-red-500 hover:text-red-700"
                    >
                      <DeleteOutlined />
                    </button>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-600 mb-1">
                          Vaccination Type
                        </label>
                        <Select
                          value={vaccination.vaccination_title}
                          onChange={value =>
                            handleVaccinationChange(
                              index,
                              'vaccination_title',
                              value
                            )
                          }
                          className="w-full"
                          placeholder="Select vaccination type"
                          allowClear
                        >
                          <Option value="Rabies">Rabies</Option>
                          <Option value="DHLPPC">DHLPPC</Option>
                          <Option value="Bordetella (kennel cough)">
                            Bordetella (kennel cough)
                          </Option>
                          <Option value="Canine Influenza Vaccine">
                            Canine Influenza Vaccine
                          </Option>
                          <Option value="Fecal Exam">Fecal Exam</Option>
                          <Option value="Leptospirosis">Leptospirosis</Option>
                          <Option value="Lyme Disease">Lyme Disease</Option>
                          <Option value="Other">Other</Option>
                        </Select>
                      </div>
                      <div>
                        <label
                          className={`block text-sm font-medium mb-1 ${
                            status.isExpired
                              ? 'text-red-600'
                              : status.isExpiringSoon
                                ? 'text-orange-600'
                                : 'text-gray-600'
                          }`}
                        >
                          Expiration Date
                          {status.isExpired && (
                            <span className="ml-2 text-xs font-bold text-red-600">
                              ⚠️ EXPIRED
                            </span>
                          )}
                          {status.isExpiringSoon && (
                            <span className="ml-2 text-xs font-bold text-orange-600">
                              ⚠️ Expires in {status.daysUntilExpiration} days
                            </span>
                          )}
                        </label>
                        <DatePicker
                          value={
                            vaccination.vaccination_date
                              ? dayjs(vaccination.vaccination_date)
                              : null
                          }
                          onChange={(date, dateString) =>
                            handleVaccinationChange(
                              index,
                              'vaccination_date',
                              dateString ? dayjs(dateString).toISOString() : ''
                            )
                          }
                          className={`w-full ${
                            status.isExpired
                              ? '[&_.ant-picker]:!border-red-500 [&_.ant-picker]:!bg-red-50'
                              : status.isExpiringSoon
                                ? '[&_.ant-picker]:!border-orange-500 [&_.ant-picker]:!bg-orange-50'
                                : ''
                          }`}
                          placeholder="Select expiration date"
                          status={
                            status.isExpired
                              ? 'error'
                              : status.isExpiringSoon
                                ? 'warning'
                                : ''
                          }
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ),
    },
    {
      key: '5',
      label: 'Documents',
      children: (
        <div className="space-y-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">Vaccination Documentation</h3>
            <Upload
              beforeUpload={handleAddDocument}
              showUploadList={false}
              accept="image/*,.pdf"
            >
              <ThemeButton
                content={
                  uploading ? (
                    <Spin size="small" />
                  ) : (
                    <>
                      <UploadOutlined className="mr-1" /> Upload Document
                    </>
                  )
                }
                className="!h-8"
              />
            </Upload>
          </div>

          {formData.attachments.length === 0 ? (
            <div className="text-center py-8 bg-gray-50 rounded-lg">
              <p className="text-gray-500">No documents uploaded</p>
              <p className="text-gray-400 text-sm mt-1">
                Upload vaccination certificates, health records, etc.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-4">
              {formData.attachments.map((doc, index) => (
                <div
                  key={index}
                  className="border rounded-lg p-4 bg-gray-50 flex items-center justify-between"
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    {doc.type === 'image' ? (
                      <img
                        src={doc.url}
                        alt={doc.name}
                        className="w-12 h-12 object-cover rounded"
                      />
                    ) : (
                      <div className="w-12 h-12 bg-primary/10 rounded flex items-center justify-center">
                        <FileOutlined className="text-primary text-xl" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{doc.name}</p>
                      <a
                        href={doc.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-primary hover:underline"
                      >
                        View
                      </a>
                    </div>
                  </div>
                  <button
                    onClick={() => handleRemoveDocument(index)}
                    className="text-red-500 hover:text-red-700 ml-2"
                  >
                    <DeleteOutlined />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      ),
    },
  ];

  return (
    <Modal
      width={800}
      title={
        <div className="text-xl font-semibold">
          Edit Dog: {data?.dog_name || 'Unknown'}
        </div>
      }
      open={isOpen}
      onCancel={onClose}
      zIndex={zIndex}
      footer={
        <div className="flex justify-between">
          {isDeleted ? (
            <button
              onClick={() => setRestoreConfirmVisible(true)}
              className="px-4 py-2 bg-green-500 text-white text-sm rounded hover:bg-green-600 transition-colors"
            >
              Restore Dog
            </button>
          ) : (
            <button
              onClick={() => setDeleteConfirmVisible(true)}
              className="px-4 py-2 bg-red-500 text-white text-sm rounded hover:bg-red-600 transition-colors"
            >
              Delete Dog
            </button>
          )}
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 border rounded hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <ThemeButton
              content={updating ? <Spin size="small" /> : 'Save Dog'}
              onClick={handleSubmit}
              className="!h-10"
            />
          </div>
        </div>
      }
      style={{ top: 20 }}
    >
      <Tabs
        items={tabItems}
        className="mt-4"
        activeKey={activeTab}
        onChange={setActiveTab}
      />

      {/* Delete Confirmation Modal */}
      <Modal
        title="Delete Dog"
        open={deleteConfirmVisible}
        onCancel={() => setDeleteConfirmVisible(false)}
        destroyOnClose
        zIndex={(zIndex || 1000) + 10}
        footer={
          <div className="flex justify-end gap-3">
            <button
              onClick={() => setDeleteConfirmVisible(false)}
              className="px-4 py-2 border rounded hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleDelete}
              disabled={deleting}
              className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 transition-colors disabled:opacity-50"
            >
              {deleting ? <Spin size="small" /> : 'Delete'}
            </button>
          </div>
        }
      >
        <div className="py-4">
          <p className="text-base mb-4">
            Are you sure you want to delete <strong>{data?.dog_name}</strong>?
          </p>
          <div className="bg-orange-50 border border-orange-200 rounded p-3 text-sm text-orange-800">
            <p className="font-semibold mb-1">Warning:</p>
            <ul className="list-disc list-inside space-y-1">
              <li>Any pending charges for this dog will be lost.</li>
              <li>This action is irreversible.</li>
            </ul>
          </div>
        </div>
      </Modal>

      {/* Restore Confirmation Modal */}
      <Modal
        title="Restore Dog"
        open={restoreConfirmVisible}
        onCancel={() => setRestoreConfirmVisible(false)}
        destroyOnClose
        zIndex={(zIndex || 1000) + 10}
        footer={
          <div className="flex justify-end gap-3">
            <button
              onClick={() => setRestoreConfirmVisible(false)}
              className="px-4 py-2 border rounded hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleRestore}
              disabled={restoring}
              className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 transition-colors disabled:opacity-50"
            >
              {restoring ? <Spin size="small" /> : 'Restore'}
            </button>
          </div>
        }
      >
        <div className="py-4">
          <p className="text-base mb-4">
            Are you sure you want to restore <strong>{data?.dog_name}</strong>?
          </p>
          <div className="bg-green-50 border border-green-200 rounded p-3 text-sm text-green-800">
            <p className="font-semibold mb-1">Note:</p>
            <p>
              This will restore the dog and make it available for editing and
              appointment booking again.
            </p>
          </div>
        </div>
      </Modal>
    </Modal>
  );
};

export default EditDogModal;
